import { useState } from "react";
import { Link } from "react-router";
import { ChevronDown } from "lucide-react";

import { formatDate, formatMoney, formatMoneyCompact } from "@/lib/format";
import {
  byScoreDesc,
  effectiveScore,
  scoreTone,
  VERDICT_LABELS,
} from "@/lib/workadvisory-score";
import { cn } from "@/lib/utils";
import { WorkRequirements } from "@/components/workadvisory/WorkRequirements";
import { registrationDeadline } from "@/lib/workadvisory-period";
import type {
  ReportedWork,
  SimilarWork,
  WorkCompetition,
} from "@/types/workadvisory";

/** El acceso se lee de un vistazo: llega bien, llega con costo, o casi no llega. */
function accessTone(score: number): string {
  if (score >= 80)
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
  if (score >= 50)
    return "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
  return "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300";
}

function locationLabel(work: ReportedWork): string {
  return [work.province, work.districtOverride ?? work.district]
    .filter(Boolean)
    .join(" · ");
}

/** Cuántos puntos tiene, cuántos alcanza y dónde está la valla. */
function Points({ analysis }: { analysis: NonNullable<ReportedWork["analysis"]> }) {
  const pct = (value: number) =>
    analysis.disponible > 0 ? (value / analysis.disponible) * 100 : 0;

  return (
    <div className="mt-1.5 w-40">
      <div className="relative h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="absolute inset-y-0 left-0 bg-emerald-500"
          style={{ width: `${pct(analysis.acreditado)}%` }}
        />
        <div
          className="absolute inset-y-0 bg-amber-400"
          style={{
            left: `${pct(analysis.acreditado)}%`,
            width: `${pct(analysis.alcanzable)}%`,
          }}
        />
        {analysis.valla != null && (
          <div
            className="absolute inset-y-0 w-0.5 bg-foreground"
            style={{ left: `${pct(analysis.valla)}%` }}
          />
        )}
      </div>
      <p className="mt-0.5 text-[10px] text-muted-foreground">
        {analysis.acreditado}
        {analysis.alcanzable > 0 && ` (+${analysis.alcanzable})`} de{" "}
        {analysis.disponible}
        {analysis.valla != null && ` · valla ${analysis.valla}`}
      </p>
    </div>
  );
}

/**
 * Las obras del informe, de mayor a menor score. Se ordena al renderizar y no
 * solo al generar, para que un score ajustado a mano quede en su sitio sin tener
 * que regenerar el informe.
 */
/**
 * "Ya hicieron algo así". Va a la vista y no dentro del desplegable: es el
 * argumento que decide si vale la pena leer el resto.
 */
function SimilarWorks({ works }: { works: SimilarWork[] }) {
  if (works.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg border-l-2 border-emerald-500 bg-emerald-50/50 px-3 py-2 dark:bg-emerald-950/20">
      <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
        Ya hicieron algo así
      </p>
      {works.map((work, index) => (
        <div key={index} className="mt-1 text-xs leading-snug">
          <span className="font-semibold">{work.nombre}</span>
          <span className="text-muted-foreground">
            {work.entidad && ` · ${work.entidad}`}
            {work.anio && ` · ${work.anio}`}
            {work.monto !== null && ` · ${formatMoneyCompact(work.monto)}`}
          </span>
          <div className="text-muted-foreground">{work.porque}</div>
        </div>
      ))}
    </div>
  );
}

/** Contra quién compiten en esta entidad, según el histórico de adjudicaciones. */
function Competition({ competition }: { competition: WorkCompetition }) {
  if (competition.winners.length === 0) return null;

  // Las señales salen del histórico, no de la IA: son conteos sobre lo que ya
  // tenemos. Solo se dicen cuando pasan un umbral — un insight que sale siempre
  // deja de leerse.
  const insights: string[] = [];

  if (competition.topTwoShare >= 0.6) {
    insights.push(
      `Los dos primeros se llevan el ${Math.round(competition.topTwoShare * 100)}% del monto: es una entidad concentrada.`,
    );
  }

  const consortiumWorks = competition.winners
    .filter((winner) => winner.isConsortium)
    .reduce((sum, winner) => sum + winner.works, 0);
  const podiumWorks = competition.winners.reduce(
    (sum, winner) => sum + winner.works,
    0,
  );
  if (podiumWorks > 0 && consortiumWorks / podiumWorks >= 0.6) {
    insights.push(
      `${consortiumWorks} de las ${podiumWorks} obras del podio las ganaron consorcios: aquí conviene asociarse.`,
    );
  }

  const leader = competition.winners[0];
  if (leader && competition.totalWorks > 0 && leader.works >= 3) {
    insights.push(
      `${leader.name} ganó ${leader.works} de ${competition.totalWorks}: hay un incumbente.`,
    );
  }

  return (
    <div className="mb-3 rounded-lg border bg-card p-3">
      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        Quién gana en esta entidad · últimos {competition.monthsBack} meses
      </p>
      <div className="mt-1.5 space-y-1">
        {competition.winners.map((winner, index) => (
          <div key={index} className="flex justify-between gap-4 text-xs">
            <span className="truncate">
              {winner.name}
              {winner.isConsortium && (
                <span className="ml-1 text-[10px] uppercase text-muted-foreground">
                  consorcio
                </span>
              )}
            </span>
            <span className="whitespace-nowrap text-muted-foreground">
              {winner.works} obra{winner.works === 1 ? "" : "s"} ·{" "}
              {formatMoneyCompact(winner.amount)}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">
        {competition.totalWorks} obras adjudicadas por{" "}
        {formatMoneyCompact(competition.totalAmount)}.
      </p>
      {insights.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {insights.map((insight, index) => (
            <li
              key={index}
              className="text-[11px] font-semibold text-amber-700 dark:text-amber-400"
            >
              · {insight}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WorksTable({ works: unsorted }: { works: ReportedWork[] }) {
  const works = [...unsorted].sort(byScoreDesc);
  // Qué obras tienen abierto su detalle. La tabla se lee de un vistazo; el
  // criterio a criterio se abre solo para la que interesa.
  const [expanded, setExpanded] = useState<number[]>([]);
  const toggle = (contractingId: number) =>
    setExpanded((current) =>
      current.includes(contractingId)
        ? current.filter((id) => id !== contractingId)
        : [...current, contractingId],
    );

  if (works.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        Ninguna obra entra en el rango de la empresa este período.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] text-left">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            <th className="pb-2 pr-4 font-semibold">Obra y entidad</th>
            <th className="pb-2 pr-4 font-semibold">Veredicto y puntaje</th>
            <th className="pb-2 pr-4 font-semibold">Ubicación</th>
            <th className="pb-2 pr-4 font-semibold">Fechas</th>
            <th className="pb-2 text-right font-semibold">Valor referencial</th>
            <th className="pb-2" />
          </tr>
        </thead>
        <tbody>
          {works.flatMap((work) => [
            <tr key={work.contractingId} className="border-t align-top">
              <td className="py-4 pr-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/contracting/${work.contractingId}`}
                    className="font-mono text-xs font-semibold text-primary hover:underline"
                  >
                    {work.nomenclature}
                  </Link>
                  <span className="text-[11px] font-semibold uppercase text-muted-foreground">
                    {work.entity}
                  </span>
                  {work.workType && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {work.workType}
                    </span>
                  )}
                  {effectiveScore(work) !== null && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                        scoreTone(effectiveScore(work)),
                      )}
                    >
                      Score {effectiveScore(work)}
                    </span>
                  )}
                  {/* El plazo para entrar. Es el dato que convierte el informe en
                      algo accionable: sin él hay que abrir cada ficha. */}
                  {registrationDeadline(work.registrationEndsAt) && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                        registrationDeadline(work.registrationEndsAt)!.urgent
                          ? "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {registrationDeadline(work.registrationEndsAt)!.label}
                    </span>
                  )}
                </div>
                <p className="mt-1 max-w-3xl text-sm leading-snug">
                  {work.description}
                </p>
                {work.reasons.map((reason, index) => (
                  <p
                    key={index}
                    className="mt-1.5 max-w-3xl text-xs leading-snug text-muted-foreground"
                  >
                    {reason}
                  </p>
                ))}

              </td>
              <td className="py-4 pr-4">
                {work.analysis && <Points analysis={work.analysis} />}
                {work.verdict ? (
                  <span
                    className={cn(
                      "inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide",
                      VERDICT_LABELS[work.verdict].tone,
                    )}
                  >
                    {VERDICT_LABELS[work.verdict].label}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </td>
              <td className="py-4 pr-4 text-xs">
                <div className="font-semibold uppercase">
                  {work.executionDepartment ?? work.department ?? "—"}
                </div>
                <div className="text-muted-foreground">
                  {locationLabel(work)}
                </div>
                {/* El acceso con su sustento: un número suelto no se puede
                    discutir, y este es el que la empresa puede corregirnos. */}
                <div className="mt-1.5">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                      accessTone(work.departmentScore),
                    )}
                  >
                    Acceso {work.departmentScore}
                  </span>
                  {/* Sin sustento de la IA el número lo puso la tabla de la
                      empresa. Se dice: un puntaje sin origen no se puede
                      discutir, y este viene de su propio criterio. */}
                  <div className="mt-1 max-w-56 leading-snug text-muted-foreground">
                    {work.accessReason ?? "Criterio interno"}
                  </div>
                </div>
              </td>
              {/* Etiqueta arriba y fecha abajo: en una columna angosta la
                  etiqueta en línea parte la fecha en dos. La pastilla de la
                  primera columna dice cuánto queda; acá va la fecha, que es lo
                  que se anota en la agenda. */}
              <td className="whitespace-nowrap py-4 pr-4 text-xs">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Convocada
                </div>
                <div className="text-muted-foreground">
                  {formatDate(work.publicatedAt)}
                </div>
                {work.registrationEndsAt && (
                  <>
                    <div className="mt-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      Registro hasta
                    </div>
                    <div
                      className={cn(
                        "font-semibold",
                        registrationDeadline(work.registrationEndsAt)?.urgent
                          ? "text-red-600 dark:text-red-400"
                          : "text-muted-foreground",
                      )}
                    >
                      {formatDate(work.registrationEndsAt)}
                    </div>
                  </>
                )}
              </td>
              <td className="py-4 text-right text-sm font-semibold tabular-nums">
                {/* Sin monto publicado manda lo que declaran las bases: es la
                    diferencia entre "no sabemos" y "vale 18 millones". Se dice de
                    dónde sale, porque no es el dato oficial de SEACE. */}
                {work.amount ? (
                  formatMoney(work.amount, "S/ ")
                ) : work.declaredAmount ? (
                  <>
                    {formatMoney(work.declaredAmount, "S/ ")}
                    <div className="text-[10px] font-normal uppercase tracking-wide text-muted-foreground">
                      según las bases
                    </div>
                  </>
                ) : (
                  "No publicado"
                )}
              </td>
              <td className="py-4 pl-2 text-right">
                {(work.competition ||
                  (work.analysis && work.analysis.rows.length > 0)) && (
                  <button
                    type="button"
                    onClick={() => toggle(work.contractingId)}
                    className="text-muted-foreground hover:text-foreground"
                    title="Ver criterio a criterio"
                  >
                    <ChevronDown
                      className={cn(
                        "size-4 transition-transform",
                        expanded.includes(work.contractingId) && "rotate-180",
                      )}
                    />
                  </button>
                )}
              </td>
            </tr>,
            expanded.includes(work.contractingId) ? (
              <tr key={`${work.contractingId}-detalle`}>
                <td colSpan={6} className="pb-4">
                  {work.analysis && (
                    <SimilarWorks works={work.analysis.obrasSimilares ?? []} />
                  )}
                  {work.competition && (
                    <Competition competition={work.competition} />
                  )}
                  {work.analysis?.resumen && (
                    <p className="mb-2 text-sm leading-relaxed">
                      {work.analysis.resumen}
                    </p>
                  )}
                  {work.analysis?.incompleto && (
                    <p className="mb-2 rounded-md bg-muted px-2.5 py-1.5 text-xs text-muted-foreground">
                      El cuadro de factores del pliego no se pudo leer completo,
                      así que el puntaje de abajo está incompleto.
                    </p>
                  )}
                  {work.analysis?.alcanzaLaValla === false && (
                    <p className="mb-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                      Aun acreditando todo lo que está a su alcance quedarían en{" "}
                      {work.analysis.acreditado + work.analysis.alcanzable} de{" "}
                      {work.analysis.valla} puntos.
                    </p>
                  )}
                  {work.analysis && (
                    <WorkRequirements rows={work.analysis.rows} />
                  )}
                </td>
              </tr>
            ) : null,
          ])}
        </tbody>
      </table>
    </div>
  );
}
